<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { ofetch } from 'ofetch'

// Pagina pubblica di raccolta contatti di un funnel. Senza login e senza il marchio della
// piattaforma: mostra solo il nome e i testi del tenant. I testi dei campi sono qui (5 lingue)
// e non nei file globali perche' la lingua della pagina la sceglie il visitatore, non il pannello.
definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const params = route.params as { appId: string; slug: string }

interface PageConfig {
  app_name: string
  primary_color: string | null
  lang: string
  languages: string[]
  title: string
  intro: string
  button_label: string
  ask_name: boolean
  ask_phone: boolean
  consent_text: string
  rtl: boolean
}

const LABELS: Record<string, Record<string, string>> = {
  it: { email: 'Email', name: 'Nome', phone: 'Telefono (facoltativo)', consent: 'Consenso', errorEmail: 'Inserisci un indirizzo email valido.', errorGeneric: 'Non siamo riusciti a inviare i dati. Riprova tra poco.', errorRate: 'Troppi tentativi ravvicinati: riprova tra un minuto.', notFound: 'Questa pagina non e\' disponibile.', privacy: 'I tuoi dati servono solo a contattarti.' },
  en: { email: 'Email', name: 'Name', phone: 'Phone (optional)', consent: 'Consent', errorEmail: 'Please enter a valid email address.', errorGeneric: 'We could not send your details. Please try again shortly.', errorRate: 'Too many attempts: please try again in a minute.', notFound: 'This page is not available.', privacy: 'Your details are only used to contact you.' },
  fr: { email: 'E-mail', name: 'Nom', phone: 'Téléphone (facultatif)', consent: 'Consentement', errorEmail: 'Saisissez une adresse e-mail valide.', errorGeneric: 'Nous n\'avons pas pu envoyer vos données. Réessayez dans un instant.', errorRate: 'Trop de tentatives : réessayez dans une minute.', notFound: 'Cette page n\'est pas disponible.', privacy: 'Vos données servent uniquement à vous contacter.' },
  ar: { email: 'البريد الإلكتروني', name: 'الاسم', phone: 'الهاتف (اختياري)', consent: 'الموافقة', errorEmail: 'أدخل عنوان بريد إلكتروني صالحا.', errorGeneric: 'تعذر إرسال بياناتك. حاول مرة أخرى بعد قليل.', errorRate: 'محاولات كثيرة: حاول مرة أخرى بعد دقيقة.', notFound: 'هذه الصفحة غير متاحة.', privacy: 'تُستخدم بياناتك فقط للتواصل معك.' },
  hr: { email: 'E-pošta', name: 'Ime', phone: 'Telefon (neobavezno)', consent: 'Privola', errorEmail: 'Unesite valjanu adresu e-pošte.', errorGeneric: 'Nismo mogli poslati vaše podatke. Pokušajte ponovno za trenutak.', errorRate: 'Previše pokušaja: pokušajte ponovno za minutu.', notFound: 'Ova stranica nije dostupna.', privacy: 'Vaši podaci služe samo za kontakt.' },
}

const config = ref<PageConfig | null>(null)
const lang = ref('')
const loading = ref(true)
const notFound = ref(false)
const submitting = ref(false)
const done = ref('')
const error = ref('')
const form = ref({ email: '', name: '', phone: '', consent: false, website: '' })

const api = ofetch.create({ baseURL: import.meta.env.VITE_API_BASE_URL || '/api', headers: { Accept: 'application/json' } })
const L = computed(() => LABELS[lang.value] ?? LABELS.it)
const accent = computed(() => config.value?.primary_color || '#0e7490')
const emailOk = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email.trim()))
const langNames: Record<string, string> = { it: 'Italiano', en: 'English', fr: 'Français', ar: 'العربية', hr: 'Hrvatski' }

const load = async (requested?: string) => {
  loading.value = true

  try {
    const data = await api<PageConfig>(`/public/funnel-lead/${params.appId}/${params.slug}`, { query: { lang: requested || undefined } })

    config.value = data
    lang.value = data.lang
  }
  catch {
    notFound.value = true
  }
  finally {
    loading.value = false
  }
}

const submit = async () => {
  error.value = ''

  if (!emailOk.value) {
    error.value = L.value.errorEmail

    return
  }

  submitting.value = true

  try {
    const res = await api<{ message: string }>(`/public/funnel-lead/${params.appId}/${params.slug}`, {
      method: 'POST',
      body: {
        email: form.value.email.trim(),
        name: config.value?.ask_name ? form.value.name.trim() || null : null,
        phone: config.value?.ask_phone ? form.value.phone.trim() || null : null,
        consent: form.value.consent,
        lang: lang.value,
        website: form.value.website,
      },
    })

    done.value = res.message
  }
  catch (e: any) {
    error.value = e?.status === 429 ? L.value.errorRate : e?.status === 422 ? L.value.errorEmail : L.value.errorGeneric
  }
  finally {
    submitting.value = false
  }
}

// Lingua iniziale: ?lang= nel link (il tenant puo' condividere una versione precisa), altrimenti quella
// del browser se la pagina la conosce; in mancanza il server usa la lingua predefinita dell'app.
const initialLang = () => {
  const fromLink = typeof route.query.lang === 'string' ? route.query.lang.slice(0, 2).toLowerCase() : ''
  const fromBrowser = (typeof navigator !== 'undefined' ? navigator.language : '').slice(0, 2).toLowerCase()

  return [fromLink, fromBrowser].find(code => code in LABELS)
}

onMounted(() => load(initialLang()))
</script>

<template>
  <div class="lead-page" :dir="config?.rtl ? 'rtl' : 'ltr'" :style="{ '--lead-accent': accent }">
    <div class="lead-card">
      <div v-if="loading" class="lead-center">
        <VProgressCircular indeterminate :color="accent" />
      </div>

      <div v-else-if="notFound || !config" class="lead-center text-medium-emphasis">
        {{ (LABELS[lang] ?? LABELS.it).notFound }}
      </div>

      <template v-else>
        <div class="lead-brand">
          {{ config.app_name }}
        </div>
        <h1 class="lead-title">
          {{ config.title }}
        </h1>
        <p v-if="config.intro" class="lead-intro">
          {{ config.intro }}
        </p>

        <div v-if="done" class="lead-thanks">
          <VIcon icon="tabler-circle-check" size="44" color="success" class="mb-2" />
          <div>{{ done }}</div>
        </div>

        <VForm v-else @submit.prevent="submit">
          <VTextField v-if="config.ask_name" v-model="form.name" :label="L.name" maxlength="120" autocomplete="name" class="mb-3" />
          <VTextField v-model="form.email" :label="L.email" type="email" maxlength="190" autocomplete="email" class="mb-3" :error="!!error && !emailOk" />
          <VTextField v-if="config.ask_phone" v-model="form.phone" :label="L.phone" maxlength="40" autocomplete="tel" class="mb-3" />
          <!-- campo civetta: nascosto agli umani, i bot lo compilano -->
          <input v-model="form.website" type="text" name="website" tabindex="-1" autocomplete="off" class="lead-hp" aria-hidden="true">
          <VCheckbox v-model="form.consent" :label="config.consent_text" hide-details class="lead-consent mb-1" />
          <div class="lead-privacy">
            {{ L.privacy }}
          </div>
          <VAlert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
            {{ error }}
          </VAlert>
          <VBtn type="submit" block size="large" :loading="submitting" class="lead-button">
            {{ config.button_label }}
          </VBtn>
        </VForm>

        <div v-if="config.languages.length > 1 && !done" class="lead-langs">
          <button v-for="l in config.languages" :key="l" type="button" class="lead-lang" :class="{ 'lead-lang--on': l === lang }" @click="load(l)">
            {{ langNames[l] ?? l }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.lead-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-block-size: 100vh;
  padding: 24px 16px;
  background: linear-gradient(160deg, #f8fafc 0%, #e2e8f0 100%);
}

.lead-card {
  position: relative;
  inline-size: 100%;
  max-inline-size: 460px;
  padding: 32px 28px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.12);
  color: #0f172a;
}

.lead-center {
  display: flex;
  align-items: center;
  justify-content: center;
  min-block-size: 160px;
  text-align: center;
}

.lead-brand {
  color: var(--lead-accent);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-block-end: 8px;
  text-transform: uppercase;
}

.lead-title {
  margin: 0 0 8px;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
}

.lead-intro {
  color: #475569;
  margin-block-end: 20px;
}

.lead-hp {
  position: absolute;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  block-size: 1px;
  inline-size: 1px;
  inset-block-start: 0;
  inset-inline-start: 0;
}

.lead-privacy {
  color: #64748b;
  font-size: 0.75rem;
  margin-block-end: 16px;
}

.lead-button {
  background: var(--lead-accent) !important;
  color: #fff !important;
}

.lead-thanks {
  padding: 28px 8px;
  text-align: center;
}

.lead-langs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-block-start: 22px;
}

.lead-lang {
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 3px 12px;
}

.lead-lang--on {
  border-color: var(--lead-accent);
  background: var(--lead-accent);
  color: #fff;
}
</style>
